import moment from 'moment'
import { repeatStringForService } from '../../helpers'
import { IClientInfo, IServiceInfo, IStopAvailabilityTimes, ITown } from '../../models'
import { IFindResultAvailabilities } from '../../models/find-result-availabilities'
import { BaseHttpService } from './base'

class TownService extends BaseHttpService {
  public getTowns = async (): Promise<ITown[]> => {
    return this.get('towns')
  }

  public getServices = async (townId: number): Promise<IServiceInfo[]> => {
    return this.get(`towns/${townId}/service-infos?mergedStops=true`)
  }

  // Upcoming departures from one stop, with the live status of each.
  public getAvailabilitiesFromStop = async (
    townId: number,
    stopId: number,
    date: Date,
    serviceInfoIds: number[]
  ): Promise<IStopAvailabilityTimes[]> => {
    const isToday = moment(date).isSame(moment(), 'day')
    const dateTime = moment(date).format(isToday ? 'YYYYMMDDHHmm' : 'YYYYMMDD0000')

    return this.get(`towns/${townId}/availabilities-from-stop/${stopId}?date-time=${dateTime}&service-info-ids=${serviceInfoIds}`)
  }

  public getClientInfo = async (townId: number): Promise<IClientInfo> => {
    return this.get(`towns/${townId}/client-info`)
  }

  public getDestinationBusStops = async (
    townId: number,
    stopId: number
  ): Promise<ITown[]> => {
    return this.get(`towns/${townId}/stops-from/${stopId}?mergedStops=true`)
  }

  // Door to door: the origin is an arbitrary point, not one of the fixed stops.
  public getDestinationBusStopsByPosition = async (
    townId: number,
    latitude: number,
    longitude: number,
    stopId?: number
  ): Promise<ITown[]> => {
    const path = `towns/${townId}/stops-from?merged_stops=true&latitude=${latitude}&longitude=${longitude}`
    return this.get(path + (stopId ? `&stop_id=${stopId}` : ''))
  }

  public getAvailabilitiesByPositions = async (
    townId: number,
    inLatitude: number,
    inLongitude: number,
    inName: string,
    outLatitude: number,
    outLongitude: number,
    outName: string,
    seats: number,
    prmSeats: number,
    date: Date,
    specificDate: boolean,
    onlyToday: boolean,
    reservationId?: number
  ): Promise<IFindResultAvailabilities> => {
    let path = `towns/${townId}/closest-availabilities-position?date_time=${moment(date).format('YYYYMMDDHHmm')}&arrival_time=${specificDate}`
    path += `&in_latitude=${inLatitude}`
    path += `&in_longitude=${inLongitude}`
    path += `&in_name=${encodeURIComponent(inName)}`
    path += `&out_latitude=${outLatitude}`
    path += `&out_longitude=${outLongitude}`
    path += `&out_name=${encodeURIComponent(outName)}`
    path += `&seats=${seats}`
    path += `&prm_seats=${prmSeats}`
    path += reservationId ? `&reservation_id=${reservationId}` : ''
    path += onlyToday ? `&only_today=${onlyToday}` : ''

    return this.get(path)
  }

  public getAvailabilitiesAndFailures = async (
    townId: number,
    originStopId: number,
    destinationStopId: number,
    exitSeats: number,
    exitPrmSeats: number,
    date: Date,
    specificDate: boolean,
    alternatives: number,
    onlyToday: boolean,
    id?: string,
  ): Promise<IFindResultAvailabilities> => {
    const exitString = repeatStringForService(
      '',
      destinationStopId,
      exitSeats
    )
  
    let exitPrmSeatsString = ''
    if (exitPrmSeats > 0) {
      exitPrmSeatsString = repeatStringForService(
        '&prm-exit-stops=',
        destinationStopId,
        exitPrmSeats
      )
    }

    let path = `towns/${townId}/v2/availabilities/${originStopId}/${exitString}?date-time=${moment(date).format('YYYYMMDDHHmm')}${exitPrmSeatsString}&arrival-time=${specificDate}`
    
    path += id ? `&reservation-id=${id}` : ''
    path += alternatives ? `&nAlternatives=${alternatives}` : ''
    path += onlyToday ? `&only-today=${onlyToday}` : ''

    return this.get(path)
  }


  public getAvailableDates = async (
    townId: number,
    startDate: Date,
    endDate: Date,
    startStopId?: number,
    endStopId?: number,
    serviceId?: number,
    isSingleStop?: boolean
  ): Promise<any> => {
    let path = 
    `towns/${townId}/available-dates?from=${moment(startDate).format('YYYY-MM-DD HH:mm')}&to=${moment(endDate).format('YYYY-MM-DD HH:mm')}`

    path += serviceId ? `&serviceId=${serviceId}` : ''
    path += startStopId ? `&startStopId=${startStopId}` : ''
    path += endStopId ? `&endStopId=${endStopId}` : ''
    path += isSingleStop ? `&single_stop=${isSingleStop}` : ''

    return this.get(path)
  }
  
  public getServicesTicketingType = async (
    townId: number
  ): Promise<any> => {
    let path = `towns/${townId}/services/ticketing-type`

    return this.get(path)
  }
}

export default new TownService()
